import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';

const LegalLayout = ({ title, lastUpdated, navLinks, children }) => {
  const { hash } = useLocation();
  const [activeSection, setActiveSection] = useState('');
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  // Simple scroll spy for sidebar
  useEffect(() => {
    const handleScroll = () => {
      const sections = navLinks.map(link => document.getElementById(link.href.replace('#', '')));
      const currentSection = sections.find(section => {
        if (!section) return false;
        const rect = section.getBoundingClientRect();
        return rect.top >= 0 && rect.top <= 200;
      });
      if (currentSection) setActiveSection(`#${currentSection.id}`);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navLinks]);

  return (
    <main className="relative min-h-screen bg-paper/30 pb-24">
      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-[68px] left-0 right-0 h-1 bg-rose z-[60] origin-left"
        style={{ scaleX }}
      />

      {/* Hero Header */}
      <div className="bg-white border-b border-line pt-16 pb-12 lg:pt-24 lg:pb-16">
        <div className="w-full max-w-[1120px] mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-[clamp(2.5rem,5vw,3.8rem)] mb-4">{title}</h1>
            <div className="flex items-center gap-3 text-sm text-muted">
              <span className="font-semibold text-aubergine">Effective:</span>
              <span>{lastUpdated}</span>
              <span className="w-1 h-1 rounded-full bg-line" />
              <span className="font-semibold text-aubergine">Last Updated:</span>
              <span>{lastUpdated}</span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="w-full max-w-[1120px] mx-auto px-4 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-16 items-start">
          {/* Sticky Sidebar */}
          <aside className="hidden lg:block sticky top-32">
            <h3 className="text-xs uppercase tracking-widest text-muted font-bold mb-6">Contents</h3>
            <nav className="flex flex-col space-y-1 border-l border-line">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`pl-4 py-2 text-[0.95rem] transition-all border-l-2 -ml-[1.5px] ${
                    activeSection === link.href 
                    ? 'border-rose text-aubergine font-bold' 
                    : 'border-transparent text-muted hover:text-aubergine hover:border-line'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Legal Content */}
          <article className="prose prose-slate max-w-none 
            prose-headings:font-serif prose-headings:text-aubergine 
            prose-h2:text-[1.8rem] prose-h2:mt-16 prose-h2:mb-6 prose-h2:pb-4 prose-h2:border-b prose-h2:border-line
            prose-h3:text-[1.2rem] prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-ink/80 prose-p:leading-relaxed prose-p:text-[1.05rem]
            prose-li:text-ink/80 prose-li:text-[1.05rem]
            prose-strong:text-aubergine
            prose-a:text-rose prose-a:no-underline hover:prose-a:underline">
            {children}
          </article>
        </div>
      </div>
    </main>
  );
};

export default LegalLayout;